import * as React from 'react';
import propz from 'propz';
import * as Yup from 'yup';
import moment from 'moment';
import { Field, FieldArray, Form, Formik } from 'formik';
import { USER_GENDER, GENDER_SERVER_TO_CLIENT_MAPPING } from '../../consts/user';
import { Autocomplete } from '../Autocomplete/Autocomplete';
import { DatePicker as SitDatePicker } from '../DatePicker/DatePicker';
import { AppUser } from 'Src/views/App/App';
import { searchFunctionForms, searchFunctionHouses, getName } from 'Src/helpers/autocomplete/autocomplete';
import { Switch } from '../Switch/Switch';
import { UnsavedChangesModal } from '../UnsavedChangesModal/UnsavedChangesModal';
import { SchoolStudent } from 'Src/models/schoolStudent';
import { MAX_NEXT_OF_KIN_PRIORITY, MIN_NEXT_OF_KIN_PRIORITY } from '../../consts/student';
import { useHistory } from 'react-router-dom';
import './StudentForm.scss';

interface Props {
  student: any;
  onSubmit: (data: any, isContinue: boolean, isSavingSimilarStudentConfirmed: boolean) => Promise<boolean>;
  onCancel: () => void;
  user: AppUser;
  isCreateStudentsChain: boolean;
  identicalStudents: SchoolStudent[];
  similarStudents: SchoolStudent[];
  isFormsExist: boolean;
  isEditStudent: boolean;
  fieldsToShow?: string[];
  searchFunctionForms?: (text: string) => Promise<any[]>;
  searchFunctionHouses?: (text: string) => Promise<any[]>;
  fullPage?: boolean;
  compact?: boolean;
}

const ALL_FIELDS = [
  'firstName',
  'lastName',
  'gender',
  'birthday',
  'form',
  'house',
  'specialEducationNeeds',
  'pupilsPremium',
  'freeSchoolMeals',
  'photoConsentOptOut',
  'medicalInfo',
  'nextOfKin'
];

const StudentEditSchema = Yup.object().shape({
  firstName: Yup.string().required('Required'),
  lastName: Yup.string().required('Required'),
  form: Yup.mixed().required('Required'),
  birthday: Yup.date(),
  nextOfKin: Yup.array().of(
    Yup.object()
      .shape({
        firstName: Yup.string(),
        email: Yup.string().email('Must be valid email')
      })
      .test('next-of-kin-first-name', 'Required', function(person: any) {
        const hasAnyDetails = Boolean(
          person &&
            [
              person.relationship,
              person.firstName,
              person.lastName,
              person.email,
              person.priority !== '0' ? person.priority : '',
              ...(person.phones || [])
            ].some(value => String(value || '').trim())
        );
        return !hasAnyDetails || Boolean(String(propz.get(person, ['firstName'], '')).trim())
          ? true
          : this.createError({ path: `${this.path}.firstName`, message: 'Required' });
      })
  )
});

const EMPTY_NEXT_OF_KIN = {
  relationship: '',
  firstName: '',
  lastName: '',
  phones: ['', ''],
  email: '',
  priority: '0'
};

const FLAG_FIELDS = [
  ['specialEducationNeeds', 'SEN', 'Special Educational Needs'],
  ['pupilsPremium', 'PP', 'Pupil Premium'],
  ['freeSchoolMeals', 'FSM', 'Free School Meals'],
  ['photoConsentOptOut', 'Photo consent', 'Opt out of published photos']
];

const getInitials = (name: string): string => {
  const parts = name
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  return (
    `${propz.get(parts, ['0', '0'], '')}${
      parts.length > 1 ? propz.get(parts, [parts.length - 1, '0'], '') : ''
    }`.toUpperCase() || '?'
  );
};

const getAvatarColor = (name: string): string => {
  const palette = ['#227ebd', '#66952e', '#f26522', '#9e2718', '#164170', '#3bafda'];
  const hash = name.split('').reduce((result, char) => (result * 31 + char.charCodeAt(0)) >>> 0, 0);
  return palette[hash % palette.length];
};

const normalizeNextOfKin = (person: any = {}) => ({
  relationship: propz.get(person, ['relationship'], ''),
  firstName: propz.get(person, ['firstName'], ''),
  lastName: propz.get(person, ['lastName'], ''),
  phones: [propz.get(person, ['phones', '0'], ''), propz.get(person, ['phones', '1'], '')],
  email: propz.get(person, ['email'], ''),
  priority: String(propz.get(person, ['priority'], 0) || 0)
});

const isEmptyNextOfKin = (person: any = {}): boolean =>
  ![
    person.relationship,
    person.firstName,
    person.lastName,
    person.email,
    person.priority !== '0' ? person.priority : '',
    ...(person.phones || [])
  ].some(value => String(value || '').trim());

const getFieldError = (touched: any, errors: any, name: string): string => {
  return propz.get(touched, name.split('.')) && propz.get(errors, name.split('.'))
    ? propz.get(errors, name.split('.'))
    : '';
};

const FieldLabel = ({ label, required = false }) => (
  <label className="sit-field__label">
    {label}
    {required ? <span className="sit-field__req">*</span> : null}
  </label>
);

const ErrorMessage = ({ message }) =>
  message ? <span className="sit-field__msg sit-field__msg--error">{message}</span> : null;

export const StudentForm: React.FunctionComponent<Props> = props => {
  const {
    student,
    onSubmit,
    onCancel,
    isCreateStudentsChain,
    identicalStudents,
    similarStudents,
    isFormsExist,
    isEditStudent,
    fieldsToShow = ALL_FIELDS,
    fullPage = false,
    compact = false
  } = props;
  const isStudentExist = typeof student !== 'undefined';
  const isEditStudentMode = isEditStudent || (isStudentExist && !isCreateStudentsChain);
  const history = useHistory();
  const submitAndContinueRef = React.useRef(false);
  const isDirtyRef = React.useRef(false);
  const pendingNavigationRef = React.useRef<string | null>(null);
  const unblockNavigationRef = React.useRef<(() => void) | null>(null);
  const [activeTab, setActiveTab] = React.useState<'details' | 'nextOfKin'>('details');
  const [isDiscardWarningOpen, setIsDiscardWarningOpen] = React.useState(false);
  const isIdenticalStudentsExist = identicalStudents.length > 0;
  const isSimilarStudentsExist = similarStudents.length > 0;
  const hasFlagFields = FLAG_FIELDS.some(([field]) => fieldsToShow.includes(field));

  const requestClose = React.useCallback(() => {
    if (isDirtyRef.current) {
      pendingNavigationRef.current = null;
      setIsDiscardWarningOpen(true);
      return;
    }

    onCancel();
  }, [onCancel]);

  const keepEditing = React.useCallback(() => {
    pendingNavigationRef.current = null;
    setIsDiscardWarningOpen(false);
  }, []);

  const discardChanges = React.useCallback(() => {
    const pendingPath = pendingNavigationRef.current;
    pendingNavigationRef.current = null;
    isDirtyRef.current = false;
    setIsDiscardWarningOpen(false);

    if (pendingPath) {
      if (unblockNavigationRef.current) {
        unblockNavigationRef.current();
        unblockNavigationRef.current = null;
      }
      history.push(pendingPath);
      return;
    }

    onCancel();
  }, [history, onCancel]);

  React.useEffect(() => {
    if (!fullPage) return undefined;

    const unblock = history.block((location: any) => {
      if (!isDirtyRef.current) return undefined;

      pendingNavigationRef.current = `${location.pathname || ''}${location.search || ''}${location.hash || ''}`;
      setIsDiscardWarningOpen(true);
      return false;
    });
    unblockNavigationRef.current = unblock;

    return () => {
      unblock();
      if (unblockNavigationRef.current === unblock) {
        unblockNavigationRef.current = null;
      }
    };
  }, [fullPage, history]);

  React.useEffect(() => {
    if (!fullPage) return undefined;

    const preventUnload = (event: BeforeUnloadEvent) => {
      if (!isDirtyRef.current) return;
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', preventUnload);
    return () => window.removeEventListener('beforeunload', preventUnload);
  }, [fullPage]);

  const getForms = (text: string, setFieldValue) => {
    const user = props.user;

    if (text === '') {
      setFieldValue('formId', '');
      setFieldValue('form', undefined);
    }

    return props.searchFunctionForms ? props.searchFunctionForms(text) : searchFunctionForms(user, text);
  };

  const getHouse = (text: string, setFieldValue) => {
    const user = props.user;

    if (text === '') {
      setFieldValue('houseId', '');
      setFieldValue('house', undefined);
    }

    return props.searchFunctionHouses ? props.searchFunctionHouses(text) : searchFunctionHouses(user, text);
  };

  const nextOfKin = isEditStudentMode ? propz.get(student, ['nextOfKin'], []).map(normalizeNextOfKin) : [];

  const studentForm = {
    firstName: isEditStudentMode ? propz.get(student, ['firstName'], '') : '',
    lastName: isEditStudentMode ? propz.get(student, ['lastName'], '') : '',
    gender: isEditStudentMode ? propz.get(student, ['gender'], USER_GENDER.MALE) : USER_GENDER.MALE,
    birthday: isEditStudentMode && typeof student.birthday !== 'undefined' ? moment(student.birthday) : '',
    formId: isEditStudentMode ? propz.get(student, ['formId'], '') : '',
    houseId: isEditStudentMode ? propz.get(student, ['houseId'], '') : '',
    specialEducationNeeds: isEditStudentMode ? propz.get(student, ['specialEducationNeeds'], false) : false,
    pupilsPremium: isEditStudentMode ? propz.get(student, ['pupilsPremium'], false) : false,
    freeSchoolMeals: isEditStudentMode ? propz.get(student, ['freeSchoolMeals'], false) : false,
    photoConsentOptOut: isEditStudentMode ? propz.get(student, ['photoConsentOptOut'], false) : false,
    medicalInfo: isEditStudentMode ? propz.get(student, ['medicalInfo'], '') : '',
    form: isEditStudentMode ? propz.get(student, ['form']) : undefined,
    house: isEditStudentMode ? propz.get(student, ['house']) : undefined,
    nextOfKin
  };

  const onSubmitForm = (values, isContinue, resetForm?) => {
    const dataCopy = { ...values };
    const isResetFormExist = typeof resetForm !== 'undefined';
    const isSavingSimilarStudentConfirmed = isSimilarStudentsExist && !isIdenticalStudentsExist;

    submitAndContinueRef.current = false;

    if (dataCopy.birthday) {
      dataCopy.birthday = moment(values.birthday).format('YYYY-MM-DD');
    } else {
      delete dataCopy.birthday;
    }

    dataCopy.nextOfKin = (values.nextOfKin || [])
      .filter(person => !isEmptyNextOfKin(person))
      .map(person => ({
        relationship: person.relationship,
        firstName: person.firstName,
        lastName: person.lastName,
        phones: (person.phones || []).filter(phone => phone !== ''),
        priority: Number(person.priority) || undefined,
        email: person.email
      }));

    return onSubmit(dataCopy, isContinue, isSavingSimilarStudentConfirmed).then(wasSaved => {
      isDirtyRef.current = false;
      if (isResetFormExist) {
        resetForm();
        setActiveTab('details');
      }
      return wasSaved;
    });
  };

  const renderDuplicateStudent = (duplicateStudent: SchoolStudent) => {
    const firstName = propz.get(duplicateStudent, ['firstName'], '');
    const lastName = propz.get(duplicateStudent, ['lastName'], '');
    const birthday = propz.get(duplicateStudent, ['birthday'], '');
    const registrationForm = propz.get(duplicateStudent, ['form', 'name'], 'Not assigned');
    return (
      <div key={duplicateStudent.id} className="student-sedit__duplicate">
        <span>{`${firstName} ${lastName}${birthday ? ` · ${birthday}` : ''}`}</span>
        <strong>Reg form: {registrationForm}</strong>
      </div>
    );
  };

  const renderSimilarStudents = () => {
    const studentToAddFirstName = propz.get(student, ['firstName'], '');
    const studentToAddLastName = propz.get(student, ['lastName'], '');
    const studentToAddBirthday = propz.get(student, ['birthday'], '');

    return (
      <div className="student-sedit__notice student-sedit__notice--warning">
        <p>There seems to be a student with similar details. Please check the data to avoid duplicating students.</p>
        <p className="font-weight-bold mb-0">The student you are adding: </p>
        <p className="text-primary">{`${studentToAddFirstName} ${studentToAddLastName} ${studentToAddBirthday}`}</p>
        <p className="font-weight-bold mb-0">The existing student(s): </p>
        <div className="student-sedit__duplicates">{similarStudents.map(renderDuplicateStudent)}</div>
      </div>
    );
  };

  const renderPriorityOptions = () => {
    const options = [
      <option key="empty_priority" value="0">
        None
      </option>
    ];

    for (let index = MIN_NEXT_OF_KIN_PRIORITY; index <= MAX_NEXT_OF_KIN_PRIORITY; index++) {
      options.push(
        <option key={index} value={index}>
          {index}
        </option>
      );
    }

    return options;
  };

  const title = isEditStudentMode ? 'Edit student' : 'Add student';

  return (
    <div
      className={`spage sedit student-sedit ${fullPage ? 'student-sedit--page' : ''} ${
        compact ? 'student-sedit--compact' : ''
      }`.trim()}
    >
      {!fullPage && !compact && (
        <button type="button" className="spage__back" onClick={requestClose}>
          <span aria-hidden="true">&lt;</span> Students
        </button>
      )}

      <Formik
        initialValues={studentForm}
        validationSchema={StudentEditSchema}
        onSubmit={(values, formikActions) => {
          return submitAndContinueRef.current
            ? onSubmitForm(values, true, formikActions.resetForm)
            : onSubmitForm(values, false);
        }}
      >
        {({ touched, errors, setFieldValue, values, handleSubmit, validateForm, dirty }) => {
          isDirtyRef.current = dirty;
          const fullName = `${values.firstName || ''} ${values.lastName || ''}`.trim();
          const formName = String(propz.get(values, ['form', 'name'], '') || '').trim();
          const houseName = String(propz.get(values, ['house', 'name'], '') || '').trim();
          const genderName = GENDER_SERVER_TO_CLIENT_MAPPING[values.gender] || String(values.gender || '');
          const birthdayLabel =
            values.birthday && moment(values.birthday).isValid() ? moment(values.birthday).format('D MMM YYYY') : '';
          const nextOfKinCount = (values.nextOfKin || []).filter(person => !isEmptyNextOfKin(person)).length;
          const enabledFlagsCount = FLAG_FIELDS.filter(([field]) => Boolean(values[field])).length;
          const hasNextOfKinTab = fullPage && fieldsToShow.includes('nextOfKin');
          const submitStudent = async (isContinue: boolean) => {
            const validationErrors: any = await validateForm();
            if (Object.keys(validationErrors).length > 0) {
              const detailErrorKeys = Object.keys(validationErrors).filter(key => key !== 'nextOfKin');
              setActiveTab(detailErrorKeys.length > 0 ? 'details' : 'nextOfKin');
            }
            submitAndContinueRef.current = isContinue;
            handleSubmit();
          };

          return (
            <Form className="student-sedit__form">
              {fullPage ? (
                <header className="student-sedit__pageHeader">
                  <div className="student-sedit__pageHeaderTop">
                    <button type="button" className="student-sedit__pageBack" onClick={requestClose}>
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M15 18l-6-6 6-6" />
                      </svg>
                      Students
                    </button>
                    <i aria-hidden="true" />
                    <span className="student-sedit__headerAvatar" title={fullName || title}>
                      {getInitials(fullName || 'Student')}
                    </span>
                    <div className="student-sedit__headerMain">
                      <span className="student-sedit__headerMode">{title}</span>
                      <h1>{fullName || 'Student details'}</h1>
                      <div className="student-sedit__headerSummary" aria-label="Student details entered so far">
                        {formName ? <span className="is-form">Form: {formName}</span> : null}
                        {houseName ? <span className="is-house">House: {houseName}</span> : null}
                        {genderName ? <span className="is-gender">{genderName}</span> : null}
                        {birthdayLabel ? <span className="is-birthday">Born {birthdayLabel}</span> : null}
                        <span className="is-kin">Next of kin: {nextOfKinCount}</span>
                        {enabledFlagsCount ? <span className="is-flags">Flags: {enabledFlagsCount}</span> : null}
                      </div>
                    </div>
                    <span className="student-sedit__recordState">
                      {isEditStudentMode ? 'Editing record' : 'New record'}
                    </span>
                  </div>
                  {hasNextOfKinTab && (
                    <nav className="student-sedit__tabs" role="tablist" aria-label="Student form sections">
                      <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'details'}
                        className={activeTab === 'details' ? 'is-active' : ''}
                        onClick={() => setActiveTab('details')}
                      >
                        Student details
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === 'nextOfKin'}
                        className={`${activeTab === 'nextOfKin' ? 'is-active' : ''} ${
                          errors.nextOfKin ? 'has-error' : ''
                        }`.trim()}
                        onClick={() => setActiveTab('nextOfKin')}
                      >
                        Next of kin <span>{nextOfKinCount}</span>
                      </button>
                    </nav>
                  )}
                </header>
              ) : (
                <div className="spage__hero">
                  <span className="sit-avatar sit-avatar--xl student-sedit__avatar" title={fullName || title}>
                    {getInitials(fullName || 'Student')}
                  </span>
                  <div className="student-sedit__heroText">
                    <div className="spage__name">{title}</div>
                    <div className="spage__meta">
                      <span>{fullName || 'Student details'}</span>
                      {formName ? (
                        <>
                          <i />
                          <span>Form {formName}</span>
                        </>
                      ) : null}
                    </div>
                  </div>
                </div>
              )}

              {!isFormsExist && (
                <div className="student-sedit__notice student-sedit__notice--danger">
                  <p>Please add school forms (classes) before creating students.</p>
                  <p>You can add forms via the School &gt; Forms tab.</p>
                  <p>
                    You can add specific forms or just use forms to reflect age groups, for instance, Year 6, Year 7
                    etc.
                  </p>
                </div>
              )}

              {isIdenticalStudentsExist && (
                <div className="student-sedit__notice student-sedit__notice--danger">
                  <p>There is already a student in the system, matching the data you have entered.</p>
                  <p>
                    If you need to change any additional details like form, house, etc. please select the existing
                    student and edit their data.
                  </p>
                  <div className="student-sedit__duplicates">{identicalStudents.map(renderDuplicateStudent)}</div>
                </div>
              )}

              {isSimilarStudentsExist && !isIdenticalStudentsExist && renderSimilarStudents()}

              <div
                className={`sedit__cols ${
                  (fullPage && activeTab === 'nextOfKin') || !hasFlagFields ? 'sedit__cols--single' : ''
                }`.trim()}
              >
                <div className="sedit__main">
                  {(!fullPage || activeTab === 'details') && (
                    <div className="sit-card sedit__section">
                      <div className="sedit__hd">Details</div>
                      <div className="sedit__grid">
                        {fieldsToShow.includes('firstName') && (
                          <div
                            className={`sit-field ${
                              getFieldError(touched, errors, 'firstName') ? 'sit-field--invalid' : ''
                            }`}
                          >
                            <FieldLabel label="First name" required />
                            <Field type="text" tabIndex="1" name="firstName" className="sit-input" />
                            <ErrorMessage message={getFieldError(touched, errors, 'firstName')} />
                          </div>
                        )}

                        {fieldsToShow.includes('lastName') && (
                          <div
                            className={`sit-field ${
                              getFieldError(touched, errors, 'lastName') ? 'sit-field--invalid' : ''
                            }`}
                          >
                            <FieldLabel label="Surname" required />
                            <Field type="text" tabIndex="2" name="lastName" className="sit-input" />
                            <ErrorMessage message={getFieldError(touched, errors, 'lastName')} />
                          </div>
                        )}

                        {fieldsToShow.includes('gender') && (
                          <div className="student-sedit__gender">
                            <span className="sit-field__label">Gender</span>
                            <div role="group" aria-label="Gender">
                              {[USER_GENDER.MALE, USER_GENDER.FEMALE].map(gender => (
                                <button
                                  type="button"
                                  key={gender}
                                  className={values.gender === gender ? 'is-selected' : ''}
                                  aria-pressed={values.gender === gender}
                                  onClick={() => setFieldValue('gender', gender)}
                                >
                                  {GENDER_SERVER_TO_CLIENT_MAPPING[gender]}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {fieldsToShow.includes('birthday') && (
                          <SitDatePicker
                            label="Date of birth"
                            mode="single"
                            value={
                              values.birthday && moment(values.birthday).isValid()
                                ? moment(values.birthday).toDate()
                                : null
                            }
                            onChange={birthdayDate => setFieldValue('birthday', birthdayDate || '')}
                            format="DD-MM-YYYY"
                            placeholder="Select date"
                            max={new Date()}
                            centered={fullPage || compact}
                            placement="bottom"
                            clearable={true}
                            error={getFieldError(touched, errors, 'birthday')}
                            className="student-sedit__birthday"
                          />
                        )}

                        {fieldsToShow.includes('form') && (
                          <div
                            className={`sit-field ${
                              getFieldError(touched, errors, 'form') ? 'sit-field--invalid' : ''
                            }`}
                          >
                            <FieldLabel label="Form" required />
                            <Field name="form">
                              {() => (
                                <Autocomplete
                                  searchFunction={text => getForms(text, setFieldValue)}
                                  getElementTitle={getName}
                                  customClass="student-sedit__autocomplete"
                                  showAllOnFocus
                                  selectTextOnFocus
                                  defaultItem={values.form}
                                  tabIndex={5}
                                  onSelect={form => {
                                    setFieldValue('formId', form.id);
                                    setFieldValue('form', form);
                                  }}
                                />
                              )}
                            </Field>
                            <ErrorMessage message={getFieldError(touched, errors, 'form')} />
                          </div>
                        )}

                        {fieldsToShow.includes('house') && (
                          <div className="sit-field">
                            <FieldLabel label="House" />
                            <Field name="house">
                              {() => (
                                <Autocomplete
                                  searchFunction={text => getHouse(text, setFieldValue)}
                                  getElementTitle={getName}
                                  customClass="student-sedit__autocomplete"
                                  showAllOnFocus
                                  selectTextOnFocus
                                  defaultItem={values.house}
                                  tabIndex={6}
                                  onSelect={house => {
                                    setFieldValue('houseId', house.id);
                                    setFieldValue('house', house);
                                  }}
                                />
                              )}
                            </Field>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {fieldsToShow.includes('nextOfKin') && (!fullPage || activeTab === 'nextOfKin') && (
                    <div className="sit-card sedit__section">
                      <FieldArray name="nextOfKin">
                        {arrayHelpers => (
                          <>
                            <div className="sedit__hd sedit__hd--row">
                              <span>
                                Next of kin
                                {values.nextOfKin.length ? (
                                  <span className="sedit__count">{values.nextOfKin.length}</span>
                                ) : null}
                                <em className="student-sedit__optional">Optional</em>
                              </span>
                              {values.nextOfKin.length ? (
                                <button
                                  type="button"
                                  className="sit-btn sit-btn--secondary"
                                  onClick={() => arrayHelpers.push({ ...EMPTY_NEXT_OF_KIN, phones: ['', ''] })}
                                >
                                  + Add next of kin
                                </button>
                              ) : null}
                            </div>

                            {values.nextOfKin.length ? (
                              <div className="sedit__kins">
                                {values.nextOfKin.map((person, index) => {
                                  const label =
                                    `${person.firstName} ${person.lastName}`.trim() || `Contact ${index + 1}`;

                                  return (
                                    <div className="sedit__kin" key={index}>
                                      <div className="sedit__kinhd">
                                        <span className="sedit__kinname">{label}</span>
                                        {person.priority && person.priority !== '0' ? (
                                          <span className="sedit__pri">Priority {person.priority}</span>
                                        ) : null}
                                        <button
                                          type="button"
                                          className="sedit__kinremove"
                                          onClick={() => arrayHelpers.remove(index)}
                                          aria-label="Remove contact"
                                        >
                                          ×
                                        </button>
                                      </div>

                                      <div className="sedit__grid student-sedit__kinGrid">
                                        <div className="sit-field">
                                          <FieldLabel label="Relationship" />
                                          <Field
                                            type="text"
                                            name={`nextOfKin.${index}.relationship`}
                                            className="sit-input"
                                            placeholder="e.g. Mother"
                                          />
                                        </div>

                                        <div className="sit-select student-sedit__priority">
                                          <label className="sit-select__label">Priority</label>
                                          <div className="sit-select__wrap">
                                            <Field
                                              component="select"
                                              name={`nextOfKin.${index}.priority`}
                                              className="sit-select__el"
                                            >
                                              {renderPriorityOptions()}
                                            </Field>
                                            <span className="sit-select__chev" aria-hidden="true">
                                              v
                                            </span>
                                          </div>
                                        </div>

                                        <div
                                          className={`sit-field ${
                                            getFieldError(touched, errors, `nextOfKin.${index}.firstName`)
                                              ? 'sit-field--invalid'
                                              : ''
                                          }`}
                                        >
                                          <FieldLabel label="First name" required />
                                          <Field
                                            type="text"
                                            name={`nextOfKin.${index}.firstName`}
                                            className="sit-input"
                                          />
                                          <ErrorMessage
                                            message={getFieldError(touched, errors, `nextOfKin.${index}.firstName`)}
                                          />
                                        </div>

                                        <div className="sit-field">
                                          <FieldLabel label="Last name" />
                                          <Field
                                            type="text"
                                            name={`nextOfKin.${index}.lastName`}
                                            className="sit-input"
                                          />
                                        </div>

                                        <div
                                          className={`sit-field student-sedit__kinEmail ${
                                            getFieldError(touched, errors, `nextOfKin.${index}.email`)
                                              ? 'sit-field--invalid'
                                              : ''
                                          }`}
                                        >
                                          <FieldLabel label="Email" />
                                          <Field type="email" name={`nextOfKin.${index}.email`} className="sit-input" />
                                          <ErrorMessage
                                            message={getFieldError(touched, errors, `nextOfKin.${index}.email`)}
                                          />
                                        </div>

                                        <div className="sedit__phones student-sedit__kinPhones">
                                          <div className="sit-field">
                                            <FieldLabel label="Primary phone" />
                                            <Field
                                              type="tel"
                                              name={`nextOfKin.${index}.phones.0`}
                                              className="sit-input"
                                            />
                                          </div>
                                          <div className="sit-field">
                                            <FieldLabel label="Alternative phone" />
                                            <Field
                                              type="tel"
                                              name={`nextOfKin.${index}.phones.1`}
                                              className="sit-input"
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <button
                                type="button"
                                className="sedit__addkin"
                                onClick={() => arrayHelpers.push({ ...EMPTY_NEXT_OF_KIN, phones: ['', ''] })}
                              >
                                + Add next of kin
                              </button>
                            )}
                          </>
                        )}
                      </FieldArray>
                    </div>
                  )}

                  {fieldsToShow.includes('medicalInfo') && (!fullPage || activeTab === 'details') && (
                    <div className="sit-card sedit__section">
                      <div className="sedit__hd">Medical information</div>
                      <Field
                        component="textarea"
                        name="medicalInfo"
                        className="sit-input sedit__textarea"
                        placeholder="Allergies, conditions, medication, instructions..."
                      />
                    </div>
                  )}
                </div>

                {hasFlagFields && (!fullPage || activeTab === 'details') && (
                  <div className="sedit__aside">
                    <div className="sit-card sedit__section">
                      <div className="sedit__hd">Flags</div>
                      <div className="sedit__flags">
                        {FLAG_FIELDS.filter(([field]) => fieldsToShow.includes(field)).map(([field, label, desc]) => (
                          <div key={field} className="sedit__flag" data-on={values[field]}>
                            <span className="sedit__flagtext">
                              <span className="sedit__flaglabel">{label}</span>
                              <span className="sedit__flagdesc">{desc}</span>
                            </span>
                            <Field name={field}>
                              {({ field: switchField }) => (
                                <Switch {...switchField} customClass="student-sedit__switch" text="" />
                              )}
                            </Field>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="sedit__footer">
                <div className="sedit__footer-inner">
                  <button
                    type="button"
                    className="student-sedit__action student-sedit__action--cancel"
                    onClick={requestClose}
                  >
                    Cancel
                  </button>
                  <div className="student-sedit__footerActions">
                    {!isEditStudentMode && (
                      <button
                        type="button"
                        className="student-sedit__action student-sedit__action--secondary"
                        tabIndex={7}
                        onClick={() => submitStudent(true)}
                        disabled={!isFormsExist}
                      >
                        Save &amp; Continue
                      </button>
                    )}
                    <button
                      type="button"
                      className="student-sedit__action student-sedit__action--primary"
                      tabIndex={8}
                      onClick={() => submitStudent(false)}
                      disabled={!isFormsExist}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
      <UnsavedChangesModal
        isOpen={isDiscardWarningOpen}
        message="This student has unsaved changes. If you leave now, those changes will be lost."
        onKeepEditing={keepEditing}
        onDiscard={discardChanges}
      />
    </div>
  );
};

StudentForm.displayName = 'StudentForm';
